import {
	activateAccount,
	deactivateAccount,
	deleteAccount,
	getUsers,
	applySettings,
	removeSettings,
	getPermissions,
	getRoles,
} from '../../../api';
import { onRequest } from '../..';
import { ActionsType, PropertyType } from '../../../common';
import { getMailReports } from '../../../api/endpoints/getMailReports';
import { getUserReportsByUser } from '../../../api/endpoints/getUserReportsByUser';
import { saveUserReports } from '../../../api/endpoints/saveUserReports';
import { filterUsers } from '../../../view/pages/Users/Users.helper';

const usersInit = async (payload: PropertyType) => {
	const { token, page = 1, perPage = '10' } = payload;
	const data = await getUsers({ token, page: 1, perPage: 1000 });
	const allUsers = data.users || [];
	const perPageNum = parseInt(perPage, 10) || 10;
	const pagesTotal = Math.ceil(allUsers.length / perPageNum) || 1;
	const currentPageNum = Math.min(Math.max(1, parseInt(`${page}`, 10) || 1), pagesTotal);
	const startIndex = (currentPageNum - 1) * perPageNum;
	const paginatedUsers = allUsers.slice(startIndex, startIndex + perPageNum);

	return {
		allUsers,
		users: paginatedUsers,
		pagesTotal,
		bulkRows: [],
		currentPage: currentPageNum,
		usersLoading: false,
	};
};

const partnersInit = (user: PropertyType) => {
	const { partners } = user;

	return { partners };
};

const permissionsInit = async (token: string) => {
	const data = await getPermissions({ token });
	return {
		permissions: data.map((r: PropertyType) => ({
			label: r.name,
			value: r.name,
			isChecked: false,
		})),
	};
};

const rolesInit = async (data: PropertyType) => {
	const roles = await getRoles(data);
	return {
		roles: roles.map((r: PropertyType) => ({
			label: r.name,
			value: r.name,
			isChecked: false,
		})),
	};
};

const onBulkChange = (state: PropertyType, id: string) => {
	const selectedRows = [...state.bulkRows];
	const isSelected = selectedRows.includes(id);

	isSelected ? selectedRows.splice(selectedRows.indexOf(id), 1) : selectedRows.push(id);

	return { bulkRows: selectedRows };
};

const onBulkAllChange = (state: PropertyType) => {
	const { users, bulkRows } = state;

	if (bulkRows.length === users.length) return { bulkRows: [] };
	return { bulkRows: users.map((u: PropertyType) => u.user_id) };
};

const onAccountDelete = async (state: PropertyType, payload: PropertyType) => {
	const { token, id } = payload;
	const success = await deleteAccount({
		token,
		userIds: [id],
	});
	const allUsers = (state.allUsers?.length ? state.allUsers : state.users || []).filter(
		(u: PropertyType) => u.user_id !== id
	);

	return { allUsers, globalErrors: [], globalSuccess: success };
};

const onAccountsDelete = async (state: PropertyType, token: string) => {
	const success = await deleteAccount({
		token,
		userIds: state.bulkRows,
	});
	const allUsers = (state.allUsers?.length ? state.allUsers : state.users || []).filter(
		(u: PropertyType) => !state.bulkRows.includes(u.user_id)
	);

	return { allUsers, globalErrors: [], globalSuccess: success };
};

const onAccountActivate = async (state: PropertyType, payload: PropertyType) => {
	const { token, id } = payload;
	const success = await activateAccount({
		token,
		userIds: [id],
	});
	const allUsers = (state.allUsers?.length ? state.allUsers : state.users || []).map((u: PropertyType) =>
		u.user_id === id ? { ...u, state: '1' } : u
	);

	return { allUsers, globalErrors: [], globalSuccess: success };
};

const onAccountsActivate = async (state: PropertyType, token: string) => {
	const success = await activateAccount({
		token,
		userIds: state.bulkRows,
	});
	const allUsers = (state.allUsers?.length ? state.allUsers : state.users || []).map((u: PropertyType) =>
		state.bulkRows.includes(u.user_id) ? { ...u, state: '1' } : u
	);

	return { allUsers, globalErrors: [], globalSuccess: success };
};

const onAccountDeactivate = async (state: PropertyType, payload: PropertyType) => {
	const { token, id } = payload;
	const success = await deactivateAccount({
		token,
		userIds: [id],
	});
	const allUsers = (state.allUsers?.length ? state.allUsers : state.users || []).map((u: PropertyType) =>
		u.user_id === id ? { ...u, state: '0' } : u
	);

	return { allUsers, globalErrors: [], globalSuccess: success };
};

const onAccountsDeactivate = async (state: PropertyType, token: string) => {
	const success = await deactivateAccount({
		token,
		userIds: state.bulkRows,
	});
	const allUsers = (state.allUsers?.length ? state.allUsers : state.users || []).map((u: PropertyType) =>
		state.bulkRows.includes(u.user_id) ? { ...u, state: '0' } : u
	);

	return { allUsers, globalErrors: [], globalSuccess: success };
};

const onOpenReports = (id: string) => {
	return { mailReportModal: id };
};

const onCloseReports = () => {
	return { mailReportModal: null, selectedReports: [] };
};

const onReportsInit = async (token: string) => {
	const mailReports = await getMailReports({
		token,
	});

	return { reports: mailReports };
};

const getReportsByUser = async (payload: PropertyType) => {
	const { token, userId } = payload;
	const reports = await getUserReportsByUser({
		token,
		userId,
	});

	return { selectedReports: Array.isArray(reports) ? reports : [], savingData: false };
};

const onSelectReports = (payload: PropertyType) => {
	return { selectedReports: payload };
};

const onSaveReports = async (state: PropertyType, token: string) => {
	const success = await saveUserReports({
		token,
		userId: state.mailReportModal,
		mailReports: state.selectedReports,
	});

	return { globalErrors: [], globalSuccess: success, selectedReports: [], mailReportModal: null, savingData: false };
};

const getSettings = (activeSettings: string[], setting: string) => {
	const settingIndex = activeSettings.indexOf(setting);

	settingIndex > -1 ? activeSettings.splice(settingIndex, 1) : activeSettings.push(setting);

	return activeSettings;
};

const onPermissionsUpdate = (state: PropertyType, payload: string) => {
	const { activePermissions } = state;

	return { activePermissions: getSettings(activePermissions, payload) };
};

const onRolesUpdate = (state: PropertyType, payload: string) => {
	const { activeRoles } = state;

	return { activeRoles: getSettings(activeRoles, payload) };
};

const onSettingsAdd = async (state: PropertyType, token: string) => {
	const success = await applySettings({
		token,
		...state,
		userIds: state.bulkRows,
	});

	return {
		globalErrors: [],
		globalSuccess: success,
		activePartners: [],
		activePermissions: [],
		activeRoles: [],
	};
};

const onSettingsRemove = async (state: PropertyType, token: string) => {
	const success = await removeSettings({
		token,
		...state,
		userIds: state.bulkRows,
	});

	return {
		globalErrors: [],
		globalSuccess: success,
		activePartners: [],
		activePermissions: [],
		activeRoles: [],
	};
};

const filterData = (state: PropertyType, page: number | string, filters: PropertyType[], perPage: number | string) => {
	const allUsers = state.allUsers?.length ? state.allUsers : state.users || [];
	const filteredUsers = filterUsers(allUsers, filters);
	const perPageNum = parseInt(`${perPage}`, 10) || 10;
	const pagesTotal = Math.ceil(filteredUsers.length / perPageNum) || 1;
	const currentPageNum = Math.min(Math.max(1, parseInt(`${page}`, 10) || 1), pagesTotal);
	const startIndex = (currentPageNum - 1) * perPageNum;
	const paginatedUsers = filteredUsers.slice(startIndex, startIndex + perPageNum);

	return {
		users: paginatedUsers,
		pagesTotal,
		filters,
		filtering: filters.length > 1,
		bulkRows: [],
		currentPage: currentPageNum,
		usersLoading: false,
	};
};

const onFilter = async (state: PropertyType, payload: PropertyType) => {
	const { page = 1, filters } = payload;

	return filterData(state, page, filters, state.perPage);
};

const onPageUpdate = async (state: PropertyType, payload: PropertyType) => {
	const { page, perPage } = payload;
	return filterData(state, page, state.filters, perPage);
};

export const actions: ActionsType = {
	LOADING: (state, payload) => ({ usersLoading: payload }),
	USERS_INIT: (state, payload) => onRequest(() => usersInit(payload), true),
	PER_PAGE: (state, payload) => ({ perPage: payload }),
	REPORTS_MODAL_OPEN: (state, payload) => onOpenReports(payload),
	REPORTS_MODAL_CLOSE: () => onCloseReports(),
	REPORTS_INIT: (state, payload) => onReportsInit(payload),
	REPORTS_USER: (state, payload) => getReportsByUser(payload),
	REPORTS_SELECT: (state, payload) => onSelectReports(payload),
	REPORTS_SAVE: (state, payload) => onSaveReports(state, payload),
	PARTNERS_INIT: (state, payload) => partnersInit(payload),
	PARTNERS_UPDATE: (state, payload) => ({ activePartners: payload }),
	PERMISSIONS_INIT: (state, payload) => onRequest(() => permissionsInit(payload), true),
	PERMISSIONS_UPDATE: (state, payload) => onPermissionsUpdate(state, payload),
	ROLES_INIT: (state, payload) => onRequest(() => rolesInit(payload), true),
	ROLES_UPDATE: (state, payload) => onRolesUpdate(state, payload),
	PAGE_UPDATE: (state, payload) => onRequest(() => onPageUpdate(state, payload), true),
	UPDATE_COLUMNS: (state, payload) => ({ columns: payload }),
	UPDATE_ROWS: (state, payload) => ({ rows: payload }),
	BULK_CHANGE: (state, payload) => onBulkChange(state, payload),
	BULK_ALL_CHANGE: (state) => onBulkAllChange(state),
	DELETE_ACCOUNT: (state, payload) => onRequest(() => onAccountDelete(state, payload), true),
	DELETE_ACCOUNTS: (state, payload) => onRequest(() => onAccountsDelete(state, payload), true),
	ACTIVATE_ACCOUNT: (state, payload) => onRequest(() => onAccountActivate(state, payload)),

	ACTIVATE_ACCOUNTS: (state, payload) => onRequest(() => onAccountsActivate(state, payload), true),
	DEACTIVATE_ACCOUNT: (state, payload) => onRequest(() => onAccountDeactivate(state, payload)),
	DEACTIVATE_ACCOUNTS: (state, payload) => onRequest(() => onAccountsDeactivate(state, payload), true),
	FILTER: (state, payload) => onFilter(state, payload),
	UPDATE_FILTERS: (state, payload) => ({ filters: payload }),
	SETTINGS_ADD: (state, payload) => onRequest(() => onSettingsAdd(state, payload), true),
	SETTINGS_REMOVE: (state, payload) => onRequest(() => onSettingsRemove(state, payload), true),
	BULK_ACTIONS_INVALIDATE: () => ({
		activePartners: [],
		activePermissions: [],
		activeRoles: [],
	}),
	SAVING: (state, payload) => ({ savingData: payload }),
	GLOBAL_ERROR: (state, payload) => ({ globalErrors: payload }),
	GLOBAL_SUCCESS: (state, payload) => ({ globalSuccess: payload }),
	MESSAGE_INVALIDATE: () => ({ globalErrors: [], globalSuccess: '', usersLoading: false }),
};
